import { Language } from '@@constants/language'

/**
 * NB-5629: wording for the refresh prompt shown when the portal's language file fails to load.
 * It cannot come from the language files (they are what failed), so each string is copied word for word
 * from the entry named in TRANSLATION_LOAD_FAILURE_SOURCE_KEYS in public/i18n/<lang>.json, and
 * translation-load-failure.spec.ts keeps the copies identical to those entries.
 * None of them starts with a first-level key, so CustomMissingTranslationHandler returns each one unchanged
 * and boxpop's `| translate` shows it as written even with no dictionary loaded.
 */
export const TRANSLATION_LOAD_FAILURE_SOURCE_KEYS = {
    title: 'services-loader.timeout-title',
    message: 'portal.app-component.connected-message',
    persistentTitle: 'portal.app-component.persistent-issue-title',
    persistentMessage: 'portal.app-component.persistent-issue-message',
    refresh: 'portal.app-component.refresh'
} as const

type TranslationLoadFailureTextName =
    keyof typeof TRANSLATION_LOAD_FAILURE_SOURCE_KEYS

export const TRANSLATION_LOAD_FAILURE_TEXTS: Record<
    Language,
    Record<TranslationLoadFailureTextName, string>
> = {
    [Language.EN]: {
        title: 'Error',
        message:
            "Please make sure you are connected to the internet to continue. Otherwise retry later.<br>Contact IN2 Support at <a href='mailto:support@joinin2.com'>support@joinin2.com</a> if it does not work.",
        persistentTitle: 'Persistent Connection Issue',
        persistentMessage:
            "This error keeps appearing. Please try: (1) Open a new Incognito window, (2) Switch to a mobile hotspot, (3) Clear your browser cache and cookies.<br>Contact IN2 Support at <a href='mailto:support@joinin2.com'>support@joinin2.com</a> if it does not work.",
        refresh: 'Refresh'
    },
    [Language.AR]: {
        title: 'خطأ',
        message:
            "يرجى التأكد من الاتصال بالإنترنت للمتابعة. إذا لم تنجح المحاولة، أعد المحاولة لاحقًا.<br>تواصل مع دعم IN2 على <a href='mailto:support@joinin2.com'>support@joinin2.com</a> إذا استمرت المشكلة.",
        persistentTitle: 'مشكلة اتصال متكررة',
        persistentMessage:
            "هذا الخطأ يتكرر باستمرار. يرجى تجربة ما يلي: (1) افتح نافذة تصفح خفي جديدة، (2) استخدم نقطة اتصال الجوال، (3) امسح ذاكرة التخزين المؤقت وملفات تعريف الارتباط.<br>تواصل مع دعم IN2 على <a href='mailto:support@joinin2.com'>support@joinin2.com</a> إذا استمرت المشكلة.",
        refresh: 'تحديث'
    },
    [Language.FR]: {
        title: 'Erreur',
        message:
            "Veuillez vous assurer d'être connecté à internet pour continuer. Sinon, veuillez réessayer plus tard.<br>Contactez le support IN2 à <a href='mailto:support@joinin2.com'>support@joinin2.com</a> si cela ne fonctionne pas.",
        persistentTitle: 'Problème de connexion persistant',
        persistentMessage:
            "Cette erreur continue d'apparaître. Veuillez essayer : (1) Ouvrir une nouvelle fenêtre de navigation privée, (2) Passer sur un hotspot mobile, (3) Vider le cache et les cookies.<br>Contactez le support IN2 à <a href='mailto:support@joinin2.com'>support@joinin2.com</a> si cela ne fonctionne pas.",
        refresh: 'Actualiser'
    },
    [Language.HI]: {
        title: 'त्रुटि',
        message:
            "कृपया जारी रखने के लिए इंटरनेट से जुड़े होने सुनिश्चित करें। अन्यथा बाद में पुन: प्रयास करें।<br>यदि यह काम नहीं करता है तो IN2 समर्थन से संपर्क करें <a href='mailto:support@joinin2.com'>support@joinin2.com</a> पर।",
        persistentTitle: 'बार-बार कनेक्शन समस्या',
        persistentMessage:
            "यह त्रुटि बार-बार आ रही है। कृपया निम्नलिखित आज़माएं: (1) नई Incognito विंडो खोलें, (2) मोबाइल हॉटस्पॉट पर स्विच करें, (3) ब्राउज़र कैश और कुकीज़ साफ़ करें।<br>यदि यह काम नहीं करता तो IN2 सपोर्ट से संपर्क करें <a href='mailto:support@joinin2.com'>support@joinin2.com</a> पर।",
        refresh: 'रीफ्रेश'
    },
    [Language.TL]: {
        title: 'Error',
        message:
            "Mangyaring tiyakin na ikaw ay nakakonekta sa internet upang magpatuloy. Kung hindi, subukan muli mamaya.<br>Makipag-ugnayan sa IN2 Support sa <a href='mailto:support@joinin2.com'>support@joinin2.com</a> kung hindi ito gumagana.",
        persistentTitle: 'Paulit-ulit na Problema sa Koneksyon',
        persistentMessage:
            "Patuloy na lumalabas ang error na ito. Pakisubukan ang mga sumusunod: (1) Magbukas ng bagong Incognito window, (2) Lumipat sa mobile hotspot, (3) I-clear ang cache at cookies ng browser.<br>Makipag-ugnayan sa IN2 Support sa <a href='mailto:support@joinin2.com'>support@joinin2.com</a> kung hindi ito gumagana.",
        refresh: 'I-refresh'
    },
    [Language.UR]: {
        title: 'خرابی',
        message:
            "برائے مہربانی جاری رکھنے کے لیے انٹرنیٹ سے منسلک ہونے کو یقینی بنائیں۔ ورنہ بعد میں دوبارہ کوشش کریں۔<br>اگر یہ کام نہیں کرتا ہے تو IN2 سپورٹ سے رابطہ کریں <a href='mailto:support@joinin2.com'>support@joinin2.com</a> پر۔",
        persistentTitle: 'مسلسل کنکشن مسئلہ',
        persistentMessage:
            "یہ خرابی بار بار آ رہی ہے۔ براہ کرم درج ذیل آزمائیں: (1) نئی Incognito ونڈو کھولیں، (2) موبائل ہاٹ اسپاٹ پر جائیں، (3) براؤزر کیش اور کوکیز صاف کریں۔<br>اگر یہ کام نہیں کرتا تو IN2 سپورٹ سے رابطہ کریں <a href='mailto:support@joinin2.com'>support@joinin2.com</a> پر۔",
        refresh: 'تازہ کریں'
    }
}

export interface TranslationLoadFailurePrompt {
    title: string
    message: string
    refresh: string
}

/** The prompt in the given language; the "keeps happening" wording once connection problems repeat. */
export function getTranslationLoadFailurePrompt(
    lang: Language,
    isPersistent: boolean
): TranslationLoadFailurePrompt {
    const texts = TRANSLATION_LOAD_FAILURE_TEXTS[lang]
    return {
        title: isPersistent ? texts.persistentTitle : texts.title,
        message: isPersistent ? texts.persistentMessage : texts.message,
        refresh: texts.refresh
    }
}

export function isSupportedLanguage(lang: string): lang is Language {
    return (Object.values(Language) as string[]).includes(lang)
}

/**
 * True when a language's words are not there at all: never loaded, or loaded empty. A dictionary that holds
 * words but lacks some keys is NOT missing; a forgotten translation keeps its fallback and never prompts.
 */
export function isDictionaryMissing(dictionary: unknown): boolean {
    return (
        !dictionary ||
        typeof dictionary !== 'object' ||
        Object.keys(dictionary).length === 0
    )
}

/**
 * Prompt only when the page itself is left without wording: the failed language is the page's current one,
 * its dictionary is missing (never arrived, or arrived empty), and it is one of the six. A failed switch to
 * another language keeps the old, loaded wording; a printout load leaves the page's own dictionary in place;
 * an unsupported code (e.g. a browser's "de") fails on every load, so prompting for it would only loop.
 */
export function shouldPromptForMissingTranslations(
    failedLang: string | undefined,
    currentLang: string,
    loadedTranslations: Record<string, unknown>
): boolean {
    return (
        !!failedLang &&
        failedLang === currentLang &&
        isDictionaryMissing(loadedTranslations?.[failedLang]) &&
        isSupportedLanguage(failedLang)
    )
}
